import React from "react";
import { ButtonProps } from "@campgladiator/cgui-core.atoms.button";
import { Modal } from "@campgladiator/cgui-core.molecules.modal";
import { TooltipProps } from "@campgladiator/cgui-core.atoms.tooltip";
import {
  ProgramScheduleDTO,
  ServicingTrainerType,
  SubscriptionDTO,
  SwapType,
  UpgradeType,
} from "@campgladiator/cg-common.types.types";
import { ConfirmUpgrade } from "./components/ConfirmUpgrade";
import {
  SelectedProductOption,
  SelectProduct,
  SelectProductProps,
} from "./components/SelectProduct";
import { TermsAndConditions } from "../TermsAndConditions";
import { UpgradeComplete } from "./components/UpgradeComplete";
import styles from "./UpgradeMembershipModal.module.scss";
import { Step } from "./useWithUpgradeMembershipModal";
import { SelectTrainer } from "./components/SelectTrainer";
import { SelectTrainerProps } from "./components/SelectTrainer/SelectTrainer";
import { TermsAndConditionsProps } from "../TermsAndConditions/TermsAndConditions";
import {
  SelectSchedule,
  SelectScheduleProps,
} from "./components/SelectSchedule";
import { SelectType, SelectTypeProps } from "./components/SelectType";
import {
  SelectSwapType,
  SelectSwapTypeProps,
} from "./components/SelectSwapType";
import { SelectedCouponOption } from "../SelectCouponInput/useSelectCouponInput";

export type UpgradeMembershipModalProps = {
  currentServicingTrainer: ServicingTrainerType["id"];
  currentTrainerName: ServicingTrainerType["name"];
  currentTrainerPhotoUrl: ServicingTrainerType["photoUrl"];
  handleOnClickViewTerms: () => void;
  hasTrainerGrants: boolean;
  isVisible: boolean;
  modalButtons: ButtonProps[];
  modalTitle: string;
  onClose: (success?: boolean) => void;
  productName?: string;
  productPrice?: string;
  selectedCouponOption?: SelectedCouponOption | null;
  selectedProductOption?: SelectedProductOption | null;
  selectedSchedule?: ProgramScheduleDTO | null;
  selectProductProps: SelectProductProps;
  selectScheduleProps: SelectScheduleProps;
  selectSwapTypeProps: SelectSwapTypeProps;
  selectTrainerProps: SelectTrainerProps;
  selectTypeProps: SelectTypeProps;
  setHasAcceptedTerms: (hasAcceptedTerms: boolean) => void;
  step: Step;
  subscriptionProductSubType: SubscriptionDTO["productSubType"];
  subscriptionProductType: SubscriptionDTO["productType"];
  swapType?: SwapType;
  termsAndConditionsProps: TermsAndConditionsProps;
  tooltip?: TooltipProps;
  updatedTrainerId?: string;
  updatedTrainerName?: string;
  updatedTrainerPhotoUrl?: string;
  upgradeCompleteSubtitle: string;
  upgradedSubscriptionProgramName?: SubscriptionDTO["productName"];
  upgradeType?: UpgradeType;
};

const UpgradeMembershipModal = ({
  hasTrainerGrants,
  isVisible,
  modalButtons,
  modalTitle,
  productName,
  productPrice = "$0.00",
  selectedCouponOption,
  selectedProductOption,
  selectedSchedule,
  selectProductProps,
  selectScheduleProps,
  selectSwapTypeProps,
  selectTrainerProps,
  selectTypeProps,
  step,
  swapType,
  termsAndConditionsProps,
  tooltip = undefined,
  updatedTrainerName = "",
  updatedTrainerPhotoUrl = "",
  upgradeCompleteSubtitle,
  handleOnClickViewTerms,
  onClose,
  setHasAcceptedTerms,
}: UpgradeMembershipModalProps) => {
  const displayModalStep = () => {
    switch (step) {
      case "selectType":
        return <SelectType {...selectTypeProps} />;
      case "selectSwapType":
        return <SelectSwapType {...selectSwapTypeProps} />;
      case "selectProduct":
        return <SelectProduct {...selectProductProps} />;
      case "selectTrainer":
        return <SelectTrainer {...selectTrainerProps} />;
      case "selectSchedule":
        return <SelectSchedule {...selectScheduleProps} />;
      case "confirmUpgrade":
        return (
          <ConfirmUpgrade
            selectedProductOption={selectedProductOption!}
            productName={productName}
            productPrice={productPrice}
            onChangeTermsCheckbox={setHasAcceptedTerms}
            onClickViewTerms={handleOnClickViewTerms}
            trainerName={updatedTrainerName}
            trainerPhotoUrl={updatedTrainerPhotoUrl}
            selectedSchedule={selectedSchedule}
            hasTrainerGrants={hasTrainerGrants}
            swapType={swapType}
          />
        );
      case "termsAndConditions":
        return <TermsAndConditions {...termsAndConditionsProps} />;
      case "upgradeSuccess":
        return <UpgradeComplete subTitle={upgradeCompleteSubtitle} />;
      default:
        return null;
    }
  };

  const modalStepClass = ["selectType", "selectSwapType"].includes(step)
    ? styles.modalSelectTypeStep
    : styles.modalDefaultStep;

  return (
    <Modal
      isVisible={isVisible}
      title={modalTitle}
      onClose={() => onClose(undefined)}
      buttons={modalButtons}
      className={modalStepClass}
      tooltip={tooltip}
    >
      {displayModalStep()}
    </Modal>
  );
};

export default UpgradeMembershipModal;
